import { Link } from 'react-router-dom'
import { Count, Money, Percent } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { KpiRow } from '../components/kpi/KpiRow'
import { KpiTile } from '../components/kpi/KpiTile'
import { today, useDashboard } from '../hooks/useReports'
import type { BatchCostReportRecord } from '@henlytics/types'

/**
 * Every figure here arrives computed from the API. Nothing on this page does
 * arithmetic on a cost (§9 rule 6).
 */
export function Dashboard() {
  const asOf = today()
  const { data, isPending, isError, error } = useDashboard(asOf)

  if (isPending) return <p className="muted">Loading…</p>
  if (isError) return <p role="alert" className="error">{error.message}</p>

  const columns: ReadonlyArray<Column<BatchCostReportRecord>> = [
    {
      key: 'code',
      header: 'Batch',
      render: (row) => <Link to={`/batches/${row.batchId}`}>{row.code}</Link>,
    },
    { key: 'status', header: 'Stage', render: (row) => (row.isLaying ? 'Laying' : 'Rearing') },
    { key: 'age', header: 'Age (wks)', numeric: true, render: (row) => Math.floor(row.ageDays / 7) },
    { key: 'birds', header: 'Birds', numeric: true, render: (row) => <Count value={row.production.population} /> },
    {
      key: 'mortality',
      header: 'Mortality',
      numeric: true,
      render: (row) => <Percent value={row.production.mortalityPct} />,
    },
    {
      key: 'production',
      header: 'Lay %',
      numeric: true,
      render: (row) => <Percent value={row.production.productionPct} />,
    },
    {
      key: 'costPerBird',
      header: 'Cost / bird',
      numeric: true,
      render: (row) => <Money value={row.cost.costPerBird?.inclOverhead ?? null} />,
    },
    {
      // §3.5 — the cash figure is the headline, and the loaded figure sits beside
      // it. Neither is ever shown without the other.
      key: 'cash',
      header: 'Cash / egg',
      numeric: true,
      render: (row) => <Money value={row.costPerEgg.cash?.inclOverhead ?? null} precision={3} />,
    },
    {
      key: 'loaded',
      header: 'Loaded / egg',
      numeric: true,
      render: (row) => <Money value={row.costPerEgg.loaded?.inclOverhead ?? null} precision={3} />,
    },
  ]

  return (
    <div className="page">
      <header className="page-head">
        <h2>Dashboard</h2>
        <p className="muted">As of {data.asOf}</p>
      </header>

      <KpiRow>
        <KpiTile label="Active batches" value={<Count value={data.totals.activeBatches} />} />
        <KpiTile label="Living birds" value={<Count value={data.totals.livingBirds} />} />
        <KpiTile
          label="Eggs today"
          value={<Count value={data.totals.eggsToday} />}
          secondary={{ label: 'Last 7 days', value: <Count value={data.totals.eggsLast7Days} /> }}
        />
      </KpiRow>

      {data.flags.length > 0 ? (
        <section className="flags">
          <h3>Data quality</h3>
          <p className="muted">
            Nothing here blocked a write. These are entries worth a second look (§3.9).
          </p>
          <ul>
            {data.flags.map((flag, index) => (
              <li key={`${flag.code}-${index}`}>
                <code>{flag.code}</code> {flag.message}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h3>Batches</h3>
        <DataTable
          rows={data.batches}
          columns={columns}
          rowKey={(row) => row.batchId}
          empty={
            <EmptyState
              title="No open batches."
              hint="Create a batch to start recording arrivals, feed and production."
              action={<Link to="/batches">Go to batches</Link>}
            />
          }
        />
      </section>
    </div>
  )
}
